import { formatDate } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { NgbDatepickerI18n, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

/**
 * Custom i18n service for datepicker.
 * By default ngbDatepicker will use LOCALE_ID, which doesn't change at runtime.
 * The only change here is to replace LOCALE_ID with TranslateService.currentLang.
 * For default, see https://github.com/ng-bootstrap/ng-bootstrap/blob/master/src/datepicker/datepicker-i18n.ts#L96
 */
@Injectable()
export class NgbDatepickerI18nCustom extends NgbDatepickerI18n {
  private translate = inject(TranslateService);

  private _monthsShort: readonly string[];
  private _monthsFull: readonly string[];

  constructor() {
    super();

    this.init();
    // We need to reset whenever language changes
    this.translate.onLangChange.subscribe(() => this.init());
  }

  init(): void {
    this._monthsShort = [...Array(12).keys()].map((month) =>
      Intl.DateTimeFormat(this.translate.getCurrentLang(), {
        month: 'short',
      }).format(new Date(2000, month))
    );

    this._monthsFull = [...Array(12).keys()].map((month) =>
      Intl.DateTimeFormat(this.translate.getCurrentLang(), {
        month: 'long',
      }).format(new Date(2000, month))
    );
  }

  getWeekdayLabel(
    weekday: number,
    width?: Exclude<Intl.DateTimeFormatOptions['weekday'], undefined>
  ): string {
    const weekdaysStartingOnSunday = [...Array(7).keys()].map((day) =>
      Intl.DateTimeFormat(this.translate.getCurrentLang(), {
        weekday: width === undefined ? 'short' : width,
      }).format(new Date(Date.UTC(2021, 5, day - 1)))
    );
    const weekdays = weekdaysStartingOnSunday.map(
      (_, index) => weekdaysStartingOnSunday[(index + 1) % 7]
    );
    return weekdays[weekday - 1] || '';
  }

  getMonthShortName(month: number): string {
    return this._monthsShort[month - 1] || '';
  }

  getMonthFullName(month: number): string {
    return this._monthsFull[month - 1] || '';
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    const jsDate = new Date(date.year, date.month - 1, date.day);
    return formatDate(jsDate, 'fullDate', this.translate.getCurrentLang());
  }
}
